import { useCallback, useEffect, useState } from 'react';

import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { DockingReference, getDockingReferences } from '../../../data_tools';
import Select from '../../common/Select';
import { IncrementDecrementInput } from '../../common/IncrementDecrementInput';
import { pointsCentroid } from '../../../util/atom_distance_utils';
import { withTooltip } from '../../common/helpers';
import { App } from '../../../BMapsApp';
import { showAlert } from '../../../utils';
import TooltipInfo from '../../common/TooltipInfo';

export function BasicTextField({
    labelText, value, onChange, validate, required=true, ...rest
}) {
    const [helperText, setHelperText] = useState('');

    useEffect(() => {
        if (validate && value !== undefined) {
            const message = value ? validate(value) : '';
            setHelperText(message);
        } else {
            setHelperText('');
        }
    }, [value, validate]);

    const handleChange = (evt) => {
        const val = evt.target.value;
        const message = val && validate ? validate(val) : '';
        if (message) {
            setHelperText(message);
        } else {
            setHelperText('');
            onChange(evt);
        }
    };

    return (
        <TextField
            fullWidth
            variant="outlined"
            value={value}
            label={labelText}
            style={{ margin: '10px 0 0 0' }}
            onChange={handleChange}
            required={required}
            error={!!helperText}
            helperText={helperText}
            {...rest}
        />
    );
}

export const TextInputWithTooltip = withTooltip(BasicTextField);

const bindingSiteTooltip = (
    <div>
        <p>
            A binding site focuses the simulation on a region: once the bulk fragments are gone,
            fragment insertions are biased toward it (location bias), and the simulation stops
            when nothing remains in it.
        </p>
        <p>
            Automatic: a 10 Å binding site around each compound in the simulation (the ligand,
            and any other molecule without a built-in definition, such as some cofactors), or
            none if there are no compounds. Custom: a sphere you define, instead. None: no
            binding site, for a broader search of the whole protein.
        </p>
    </div>
);

export const customBindingSiteIncompleteText = 'Custom binding site: pick a reference or enter'
    + ' specs, or choose Automatic or None, to prepare the system.';

/**
 * BindingSiteSpecs - Chooses the binding site for a bmoc simulation: automatic (bfd-server
 * derives one from the simulation's compounds), custom (a sphere around the centroid of a
 * reference object, or typed specs), or none.  Calls setValue with
 * { bindingSiteSpecs, autoBindingSite, incomplete } for write-project-case; incomplete is true
 * while Custom has no site yet (no reference picked, no specs typed), so the form can hold off
 * preparing rather than run with a choice other than the one shown.
 */
export function BindingSiteSpecs({
    setValue: setValues,
    selectionAlreadyUsed=false,
    disabled=false,
    automaticHint='',
}) {
    const refList = getDockingReferences(App.Workspace, { includeCompounds: true });
    const [mode, setMode] = useState('auto'); // auto | custom | none
    const open = mode === 'custom';
    // Custom specs; empty until a reference is picked or specs are typed.
    const setValue = useCallback((bindingSiteSpecs) => {
        setValues({
            bindingSiteSpecs, autoBindingSite: true, incomplete: !bindingSiteSpecs.trim(),
        });
    }, [setValues]);
    const [bindingSiteRadius, setBindingSiteRadius] = useState(15);
    const [inputType, setInputType] = useState('picker');
    const [inputSpecs, setInputSpecs] = useState('');
    const [selectedRef, setSelectedRef] = useState(DockingReference.UNSPECIFIED_ID);

    const myRef = refList.find((r) => r.id === selectedRef);
    const customIncomplete = inputType === 'picker'
        ? selectedRef === DockingReference.UNSPECIFIED_ID
        : !inputSpecs.trim();

    useEffect(() => {
        if (selectionAlreadyUsed && myRef?.type === DockingReference.Types.selected) {
            setInputSpecs('');
            setBindingSiteRadius(15);
            setSelectedRef(DockingReference.UNSPECIFIED_ID);
            setMode('auto');
            setValues({ bindingSiteSpecs: '', autoBindingSite: true, incomplete: false });
            showAlert('Binding Site Specs', 'Warning: the custom binding site has been removed because the selected atoms are now being used to define the simulation protein. The binding site is back to Automatic.');
        }
    }, [selectionAlreadyUsed, setValues, selectedRef]);

    const updateText = useCallback((reference, radius) => {
        const centroid = pointsCentroid(reference.atoms);
        const radiusAsNumber = Number(radius);
        // Tracking inputSpecs and value separately allow us to preserve full precision for value
        setInputSpecs(`${radiusAsNumber} center ${centroid.map((value) => value.toFixed(2)).join(' ')}`);
        setValue(`${radiusAsNumber} center ${centroid.join(' ')}`);
    }, [setValue]);

    const handleFormchange = (action, payload={}) => {
        switch (action) {
            case 'auto':
                setMode('auto');
                setValues({ bindingSiteSpecs: '', autoBindingSite: true, incomplete: false });
                break;
            case 'none':
                setMode('none');
                setValues({ bindingSiteSpecs: '', autoBindingSite: false, incomplete: false });
                break;
            case 'custom':
                setMode('custom');
                handleFormchange(inputType === 'picker' ? 'usePicker' : 'useTextinput');
                break;
            case 'useTextinput':
                setInputType('textinput');
                setValue(inputSpecs);
                break;
            case 'usePicker': {
                setInputType('picker');
                if (selectedRef === DockingReference.UNSPECIFIED_ID) {
                    setValue(''); // nothing picked yet
                    return;
                }
                updateText(myRef, bindingSiteRadius);
                break;
            }
            case 'textInputChanged':
                setInputSpecs(payload.inputSpecs);
                setValue(payload.inputSpecs);
                setSelectedRef(DockingReference.UNSPECIFIED_ID);
                break;
            case 'radiusChanged': {
                setBindingSiteRadius(payload.radius);
                if (selectedRef === DockingReference.UNSPECIFIED_ID) return;
                updateText(myRef, payload.radius);
                break;
            }
            case 'referenceChanged': {
                updateText(payload.reference, bindingSiteRadius);
                break;
            }
            default:
                break;
        }
    };

    return (
        <>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                Binding Site
                <TooltipInfo title={bindingSiteTooltip} />
            </Box>
            <RadioGroup
                value={mode}
                onChange={(evt) => handleFormchange(evt.target.value)}
                style={{ flexDirection: 'row' }}
            >
                <FormControlLabel
                    control={<Radio disabled={disabled} />}
                    label="Automatic"
                    value="auto"
                />
                <FormControlLabel
                    control={<Radio disabled={disabled} />}
                    label="Custom"
                    value="custom"
                />
                <FormControlLabel
                    control={<Radio disabled={disabled} />}
                    label="None (broader search)"
                    value="none"
                />
            </RadioGroup>
            { (mode === 'auto' && !disabled && automaticHint) && (
                <Typography variant="body2" sx={{ margin: '0 1rem', color: 'text.secondary' }}>
                    {automaticHint}
                </Typography>
            )}
            { (open && !disabled && customIncomplete) && (
                <Typography variant="body2" sx={{ margin: '0 1rem', color: 'warning.main' }}>
                    {customBindingSiteIncompleteText}
                </Typography>
            )}
            { (open && !disabled) && (
                <Box sx={{ margin: '0 1rem' }}>
                    <RadioGroup
                        onChange={(evt) => {
                            handleFormchange(evt.target.value);
                        }}
                        style={{ flexDirection: 'row' }}
                    >
                        <FormControlLabel
                            control={<Radio checked={inputType==='picker'} />}
                            label="Use Reference Object"
                            value="usePicker"
                        />
                        <FormControlLabel
                            control={<Radio checked={inputType==='textinput'} />}
                            label="Use Text Input"
                            value="useTextinput"
                        />
                    </RadioGroup>
                    { inputType === 'picker' ? (
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            marginTop: '10px',
                            alignItems: 'center',
                        }}
                        >
                            <BindingSiteSelector
                                refList={refList}
                                value={selectedRef}
                                onChange={(evt) => {
                                    setSelectedRef(evt.target.value);
                                    const newRef = refList.find((r) => r.id === evt.target.value);
                                    handleFormchange('referenceChanged', { reference: newRef });
                                }}
                                optionDisabledReasonFn={(ref) => (
                                    (ref.type === DockingReference.Types.selected
                                        && selectionAlreadyUsed)
                                        ? 'Selected atoms are already being used to define the simulation protein'
                                        : null)}
                                label="Binding Site Reference"
                                style={{ flex: '1 1 50%', marginRight: '2em' }}
                            />
                            <Box style={{ flex: '1 1 30%', display: 'flex', alignItems: 'center' }}>
                                <Box>
                                    <InputLabel htmlFor="bindingSiteSpecsRadius">Radius (Å)</InputLabel>
                                </Box>
                                <Box>
                                    <IncrementDecrementInput
                                        name="Radius"
                                        min={1}
                                        value={bindingSiteRadius}
                                        setValue={(value) => handleFormchange('radiusChanged', { radius: value })}
                                        incrementInputProps={{ id: 'bindingSiteSpecsRadius' }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <TextInputWithTooltip
                                id="bindingSiteSpec"
                                labelText="Binding Site Specification"
                                tooltip={(
                                    <div>
                                        <p>Format:</p>
                                        <code>
                                            {'<radius> center <x> <y> <z>'}
                                        </code>
                                        <br />
                                        <code>
                                            {'<radius> <res 1> [<atom 1>] <res 2> [<atom 2>] ...'}
                                        </code>
                                        <p>
                                            The center is the centroid of the listed atoms
                                            (all the atoms of a residue when no atom is given).
                                        </p>
                                        <p>Example:</p>
                                        <code>15 GLU.23:A SER.30:A</code>
                                        <p>See the Guide for more information.</p>
                                    </div>
                                )}
                                required={false}
                                value={inputSpecs}
                                onChange={(evt) => {
                                    handleFormchange('textInputChanged', { inputSpecs: evt.target.value });
                                }}
                            />
                        </>
                    )}
                </Box>
            )}
        </>
    );
}

/**
 * BindingSiteSelector - A component to choose a binding site reference from
 * compounds, hotspots, or selected atoms.
 * This is from DockingForm DockingReferenceSelect and could be refactored into a common component.
 */
export function BindingSiteSelector({
    refList,
    optionDisabledReasonFn,
    placeholder='Choose binding site reference',
    includeCompounds=true,
    allowNoneProps,
    ...rest
}) {
    const options = [
        <option
            key={DockingReference.UNSPECIFIED_ID}
            value={DockingReference.UNSPECIFIED_ID}
            title=""
            disabled
        >
            {placeholder}
        </option>,
    ];
    if (allowNoneProps) {
        const {
            title='No binding site reference',
            text='No binding site reference',
        } = allowNoneProps;
        options.push(
            <option
                key={DockingReference.NONE_ID}
                value={DockingReference.NONE_ID}
                title={title}
            >
                {text}
            </option>,
        );
    }
    refList.forEach((r) => {
        let title = r.type === DockingReference.Types.hotspot
            ? 'Energy is the average excess chemical potential of the hotspot fragments'
            : '';

        const disabledReason = optionDisabledReasonFn && optionDisabledReasonFn(r);
        if (disabledReason) {
            title = disabledReason;
        }
        options.push(
            <option
                key={r.description}
                value={r.id}
                title={title}
                disabled={!!disabledReason}
            >
                {r.description}
            </option>
        );
    });

    // When a type of docking reference isn't available, dummy options in
    // the selector give an affordance that the possibility is there.
    const dummyInfo = [
        {
            type: DockingReference.Types.selected,
            text: 'Selected atoms',
            title: 'Click on atoms in the 3D workspace to define a center from selected atoms.',
        }, {
            type: DockingReference.Types.compound,
            text: includeCompounds ? 'Ligand / compound' : 'Crystal ligand',
            title: `This protein structure has no crystal ligand to use as a reference.${includeCompounds ? '  Add and/or dock a compound to use it as a binding site reference.' : ''}`,
        }, {
            type: DockingReference.Types.hotspot,
            text: 'Hotspot',
            title: 'There are no hotspots to use as references. Run a clustering simulation to generate hotspots.',
        },
    ];
    // Add the dummy options if neccessary
    dummyInfo.forEach(({ type, text, title }) => {
        const found = refList.find((r) => r.type === type);
        if (!found) {
            options.push(
                <option key={`DummyOption:${text}`} disabled title={title}>{text}</option>,
            );
        }
    });

    return (
        <Select {...rest}>
            {options}
        </Select>
    );
}
